const AI_BUSY_ATTRIBUTE = "data-apk-games-ai-busy";
const AI_BUSY_STYLE_ID = "apk-games-ai-interaction-lock-style";
const AI_BUSY_CHANGE_EVENT = "apk-games:ai-busy-change";

let activeAiCalls = 0;
let interactionGuardInstalled = false;

export class AiInteractionBusyError extends Error {
  constructor() {
    super("已有 AI 请求正在处理中，请等待本次操作完成");
    this.name = "AiInteractionBusyError";
  }
}

function isBlockedControl(target: EventTarget | null) {
  if (typeof Element === "undefined" || !(target instanceof Element)) return false;
  if (target.closest('[data-ai-busy-allow="true"]')) return false;
  return Boolean(target.closest('button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]'));
}

function blockBusyInteraction(event: Event) {
  if (!isAiInteractionLocked()) return;
  if (event.type === "submit" || isBlockedControl(event.target)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  }
}

function blockBusyKeyboardInteraction(event: KeyboardEvent) {
  if (event.key !== "Enter" && event.key !== " ") return;
  blockBusyInteraction(event);
}

function ensureInteractionGuard() {
  if (interactionGuardInstalled || typeof document === "undefined") return;
  interactionGuardInstalled = true;
  document.addEventListener("click", blockBusyInteraction, true);
  document.addEventListener("submit", blockBusyInteraction, true);
  document.addEventListener("keydown", blockBusyKeyboardInteraction, true);

  if (!document.getElementById(AI_BUSY_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = AI_BUSY_STYLE_ID;
    style.textContent = `
      html[${AI_BUSY_ATTRIBUTE}="true"] button:not([data-ai-busy-allow="true"]),
      html[${AI_BUSY_ATTRIBUTE}="true"] [role="button"]:not([data-ai-busy-allow="true"]),
      html[${AI_BUSY_ATTRIBUTE}="true"] input[type="button"]:not([data-ai-busy-allow="true"]),
      html[${AI_BUSY_ATTRIBUTE}="true"] input[type="submit"]:not([data-ai-busy-allow="true"]) {
        cursor: wait !important;
        opacity: 0.58 !important;
        filter: saturate(0.65) !important;
      }
    `;
    document.head.appendChild(style);
  }
}

function publishBusyState(busy: boolean) {
  if (typeof document !== "undefined") {
    if (busy) document.documentElement.setAttribute(AI_BUSY_ATTRIBUTE, "true");
    else document.documentElement.removeAttribute(AI_BUSY_ATTRIBUTE);
  }
  if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(new CustomEvent(AI_BUSY_CHANGE_EVENT, { detail: { busy } }));
  }
}

/**
 * Locks game buttons for the full lifetime of a shared LLM request. The returned
 * release function is idempotent so nested error and cleanup paths cannot unlock early.
 */
export function acquireAiInteractionLock() {
  ensureInteractionGuard();
  activeAiCalls += 1;
  if (activeAiCalls === 1) publishBusyState(true);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeAiCalls = Math.max(0, activeAiCalls - 1);
    if (activeAiCalls === 0) publishBusyState(false);
  };
}

export function isAiInteractionLocked() {
  return activeAiCalls > 0;
}

export { AI_BUSY_ATTRIBUTE, AI_BUSY_CHANGE_EVENT };
